'use client';

import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { CategoryIcon } from '@/components/CategoryIcon';
import { useAppSettings } from '@/hooks/useAppSettings';
import { formatMoney } from '@/lib/money';

type DonutSegment = {
  id: string;
  label: string;
  value: number;
  color: string;
  icon?: string;
};

type DonutChartProps = {
  title: string;
  subtitle?: string;
  segments: DonutSegment[];
  emptyLabel: string;
  centerLabel?: string;
  centerValue?: string;
};

export default function DonutChart({ title, subtitle, segments, emptyLabel, centerLabel, centerValue }: DonutChartProps) {
  const { settings } = useAppSettings();
  const money = (value: number) => formatMoney(value, settings.currency, 0);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div className="chart-card rounded-2xl border border-slate-800 bg-slate-950 p-4">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-100">{title}</h3>
        {subtitle ? <p className="mt-1 text-sm text-slate-400">{subtitle}</p> : null}
      </div>

      {total <= 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-5 text-sm text-slate-400">{emptyLabel}</div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-[210px_1fr] sm:items-center">
          <div className="no-print pointer-events-none relative mx-auto h-52 w-52 select-none">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={segments}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={58}
                  outerRadius={86}
                  paddingAngle={segments.length > 1 ? 3 : 0}
                  cornerRadius={10}
                  stroke="rgb(2 6 23)"
                  strokeWidth={4}
                  isAnimationActive={false}
                  activeShape={false}
                >
                  {segments.map((segment) => (
                    <Cell key={segment.id} fill={segment.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-xs font-medium text-slate-400">{centerLabel ?? 'Total'}</span>
              <span className="mt-1 text-xl font-semibold text-slate-100">{centerValue ?? money(total)}</span>
            </div>
          </div>
          <div className="print-only pointer-events-none relative mx-auto h-52 w-52 select-none">
            <PieChart width={208} height={208}>
              <Pie
                data={segments}
                dataKey="value"
                nameKey="label"
                innerRadius={58}
                outerRadius={86}
                paddingAngle={segments.length > 1 ? 3 : 0}
                cornerRadius={10}
                stroke="rgb(255 255 255)"
                strokeWidth={4}
                isAnimationActive={false}
                activeShape={false}
              >
                {segments.map((segment) => (
                  <Cell key={segment.id} fill={segment.color} />
                ))}
              </Pie>
            </PieChart>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-xs font-medium text-slate-400">{centerLabel ?? 'Total'}</span>
              <span className="mt-1 text-xl font-semibold text-slate-100">{centerValue ?? money(total)}</span>
            </div>
          </div>

          <div className="space-y-3">
            {segments.map((segment) => {
              const percent = Math.round((segment.value / total) * 100);

              return (
                <div key={segment.id} className="flex items-center justify-between gap-3 rounded-2xl bg-slate-900/70 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-3">
                    {segment.icon ? (
                      <CategoryIcon icon={segment.icon} color={segment.color} className="h-8 w-8" />
                    ) : (
                      <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-200">{segment.label}</p>
                      <p className="text-xs text-slate-500">{percent}% du total</p>
                    </div>
                  </div>
                  <p className="shrink-0 text-sm font-semibold text-slate-100">{money(segment.value)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
